import clsx from "clsx";
import { X } from "lucide-react";
import type { ReactNode } from "react";

import { HelpButton } from "../help/HelpButton";
import { type HelpScreen, VIEW_SCREENS } from "../help/topics";
import { t } from "../i18n/i18n";
import type { ViewKey } from "../state/ui";
import { IconButton } from "./ui";
import { ViewIcon, viewLabel } from "./viewMeta";

/** En-tête d'une vue plein cadre (R7) : 41 px (barre d'outils de Prevail), filet en bas, fond blanc. Dans cet
 *  ordre : icône (16 px) et titre de la navigation (`VIEW_META`, sauf `title`), nom du dossier (`context`, gris,
 *  tronqué), `badges`, puis à droite les `actions` (boutons `Button size="sm"` ou `IconButton`), la bascule de
 *  volet (`panelToggle`), le « ? » de l'aide de la vue et, si `onClose`, la croix. Une seule ligne tant que tout
 *  tient ; faute de place, les actions passent sous le titre (deux lignes au plus, le bloc du titre garde au moins
 *  280 px).
 *
 *  `help` : écran d'aide (par défaut celui de la vue, `VIEW_SCREENS`), `false` pour aucun « ? » ; `helpTopic` :
 *  fiche à ouvrir. */
export function ViewHeader({
  view,
  title,
  context,
  badges,
  actions,
  panelToggle,
  onClose,
  help,
  helpTopic,
  className,
}: {
  view: ViewKey;
  title?: ReactNode;
  context?: ReactNode;
  badges?: ReactNode;
  actions?: ReactNode;
  panelToggle?: ReactNode;
  onClose?: () => void;
  help?: HelpScreen | false;
  helpTopic?: string;
  className?: string;
}) {
  const screen = help === false ? null : (help ?? VIEW_SCREENS[view] ?? null);
  return (
    <header
      className={clsx(
        "flex min-h-[41px] shrink-0 items-center gap-x-2 border-b border-line bg-surface px-4 py-1",
        "flex-wrap gap-y-1",
        className,
      )}
    >
      <div className="flex min-w-[min(100%,280px)] flex-1 items-center gap-2">
        <ViewIcon view={view} className="shrink-0 text-ink" />
        <h1 className="shrink-0 text-title leading-snug font-semibold text-ink">{title ?? viewLabel(view)}</h1>
        {context !== undefined && context !== null && context !== "" && (
          // Nom tronqué faute de place : entier dans l'info-bulle.
          <span className="min-w-0 truncate text-ui text-ink-muted" title={typeof context === "string" ? context : undefined}>
            {context}
          </span>
        )}
        {badges && <span className="flex shrink-0 items-center gap-1.5">{badges}</span>}
      </div>
      <div className="ml-auto flex shrink-0 items-center justify-end gap-1.5">
        {actions}
        {panelToggle}
        {help !== false && <HelpButton screen={screen} topic={helpTopic} size={16} />}
        {onClose && (
          <IconButton onClick={onClose} title={t("Fermer")} aria-label={t("Fermer")}>
            <X size={16} />
          </IconButton>
        )}
      </div>
    </header>
  );
}
